import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore, getUserById } from '../store';
import { Avatar } from '../components/ui/Avatar';
import { SkillEditor, SkillBar } from '../components/ui/SkillBar';
import { BountyCard } from '../components/missions/BountyCard';
import { MissionDetailModal } from '../components/missions/MissionDetailModal';
import { Stars } from '../components/ui/Stars';
import { Skill, Mission, COUNTRIES } from '../types';
import { t } from '../lib/i18n';
import { sanitize, clamp } from '../lib/security';
import { db } from '../lib/db';
import toast from 'react-hot-toast';

type Tab = 'edit' | 'missions' | 'active';

export const ProfilePage: React.FC = () => {
  const { currentUser, updateCurrentUser, refreshMissions, missions, lang } = useStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('edit');
  const [selectedMission, setSelectedMission] = useState<Mission | null>(null);

  // Edit form state (mirrors currentUser)
  const [firstName, setFirstName] = useState(currentUser?.firstName ?? '');
  const [lastName, setLastName] = useState(currentUser?.lastName ?? '');
  const [country, setCountry] = useState(currentUser?.country ?? 'France');
  const [city, setCity] = useState(currentUser?.city ?? '');
  const [bio, setBio] = useState(currentUser?.bio ?? '');
  const [skills, setSkills] = useState<Skill[]>(currentUser?.skills ?? []);

  if (!currentUser) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <span className="icon">🔒</span>
          <p>Sign in to view your profile.</p>
          <button className="btn btn-gold" onClick={() => navigate('/')}>Go Home</button>
        </div>
      </div>
    );
  }

  const myMissions = missions.filter(m => m.posterId === currentUser.id);
  const activeMissions = missions.filter(m => m.hiredId === currentUser.id && m.status !== 'done' && m.status !== 'cancelled');

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    updateCurrentUser({
      firstName: sanitize(clamp(firstName.trim(), 50)),
      lastName: sanitize(clamp(lastName.trim(), 50)),
      country,
      city: sanitize(clamp(city.trim(), 80)),
      bio: sanitize(clamp(bio.trim(), 300)),
      skills: skills.filter(s => s.name.trim()).map(s => ({
        name: sanitize(clamp(s.name.trim(), 40)),
        level: Math.min(10, Math.max(1, s.level)),
      })),
    });
    refreshMissions();
    toast.success(t(lang, 'toast_profile_saved'));
  };

  const handleAvatarUpload = (dataUrl: string) => {
    updateCurrentUser({ avatar: dataUrl });
    toast.success('Profile picture updated!');
  };

  return (
    <div className="page-container">
      {/* Profile banner */}
      <div className="profile-banner">
        <Avatar
          user={currentUser}
          size={100}
          editable
          onUpload={handleAvatarUpload}
          onError={(k) => toast.error(t(lang, k))}
        />
        <div className="profile-banner-info">
          <h2>{sanitize(currentUser.firstName)} {sanitize(currentUser.lastName)}</h2>
          <p>📍 {sanitize(currentUser.city)}, {sanitize(currentUser.country)}</p>
          <Stars rating={currentUser.rating} count={currentUser.ratingCount} />
          {currentUser.bio && <p className="profile-bio-preview">"{sanitize(currentUser.bio)}"</p>}
        </div>
        <div className="profile-stats">
          <div className="stat-box sm">
            <div className="stat-num">{myMissions.length}</div>
            <div className="stat-label">Bounties</div>
          </div>
          <div className="stat-box sm">
            <div className="stat-num">{activeMissions.length}</div>
            <div className="stat-label">Active</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-bar">
        {(['edit', 'missions', 'active'] as Tab[]).map(tb => (
          <button
            key={tb}
            className={`tab ${tab === tb ? 'active' : ''}`}
            onClick={() => setTab(tb)}
          >
            {tb === 'edit' ? `✏️ ${t(lang, 'profile_edit')}` :
             tb === 'missions' ? `📋 ${t(lang, 'profile_missions')}` :
             `⚡ ${t(lang, 'profile_active')}`}
          </button>
        ))}
      </div>

      {/* Edit form */}
      {tab === 'edit' && (
        <div className="form-card">
          <form onSubmit={handleSave} noValidate>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t(lang, 'auth_firstname')}</label>
                <input className="form-input" value={firstName} onChange={e => setFirstName(e.target.value)} maxLength={50} required />
              </div>
              <div className="form-group">
                <label className="form-label">{t(lang, 'auth_lastname')}</label>
                <input className="form-input" value={lastName} onChange={e => setLastName(e.target.value)} maxLength={50} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t(lang, 'auth_country')}</label>
                <select className="form-input" value={country} onChange={e => setCountry(e.target.value)}>
                  {COUNTRIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t(lang, 'auth_city')}</label>
                <input className="form-input" value={city} onChange={e => setCity(e.target.value)} maxLength={80} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t(lang, 'profile_bio')} <span className="form-hint">({bio.length}/300)</span></label>
              <textarea className="form-input" value={bio} onChange={e => setBio(e.target.value)} rows={3} maxLength={300}
                placeholder="Tell mercenaries what you're looking for…" />
            </div>
            <div className="form-group">
              <label className="form-label">
                {t(lang, 'profile_skills')}
                <span className="form-hint" style={{ marginLeft: 8 }}>{t(lang, 'profile_avatar_limit')}</span>
              </label>
              <SkillEditor
                skills={skills}
                onChange={setSkills}
                addLabel={t(lang, 'profile_add_skill')}
                onError={(k) => toast.error(t(lang, k))}
              />
            </div>
            <button className="btn btn-gold btn-full" type="submit">
              💾 {t(lang, 'profile_save')}
            </button>
          </form>
        </div>
      )}

      {/* My missions */}
      {tab === 'missions' && (
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <button className="btn btn-gold btn-sm" onClick={() => navigate('/missions/create')}>
              ✍️ Post New Bounty
            </button>
          </div>
          {myMissions.length === 0 ? (
            <div className="empty-state">
              <span className="icon">🌵</span>
              <p>{t(lang, 'empty_missions')}</p>
            </div>
          ) : (
            <div className="grid-2">
              {myMissions.map(m => (
                <BountyCard key={m.id} mission={m} poster={currentUser} onClick={() => setSelectedMission(m)} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Active missions (hired on) */}
      {tab === 'active' && (
        activeMissions.length === 0 ? (
          <div className="empty-state">
            <span className="icon">⚡</span>
            <p>{t(lang, 'empty_active')}</p>
          </div>
        ) : (
          <div className="grid-2">
            {activeMissions.map(m => {
              const poster = getUserById(m.posterId);
              return <BountyCard key={m.id} mission={m} poster={poster} onClick={() => setSelectedMission(m)} />;
            })}
          </div>
        )
      )}

      <MissionDetailModal
        mission={selectedMission}
        open={!!selectedMission}
        onClose={() => setSelectedMission(null)}
        onOpenAuth={() => {}}
        onReport={() => {}}
      />
    </div>
  );
};
